import { TextLink } from '@edsn/pie-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Card } from '#pie/components/card/Card';
import { CprContactGroupDetailExport } from '#pie/components/cpr-export/CprContactGroupDetailExport';
import { DataFilters } from '#pie/components/data-filters/DataFilters';
import { DataTable } from '#pie/components/data-table/DataTable';
import { DataToolbar } from '#pie/components/data-toolbar/DataToolbar';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { useDataView } from '#pie/hooks/useDataView';
import { useMarketRoleOptions } from '#pie/hooks/useMarketRoleOptions';
import type { MarketRole } from '#pie/internal/api/pie-bff';
import {
  getCprContactFilters,
  getCprContacts,
  useGetCprContactFilters,
} from '#pie/internal/api/pie-bff';
import {
  cprContactGroupDetailFiltersAtom,
  cprContactGroupDetailSortingAtom,
} from '#pie/stores/cprContactGroupDetail';
import { mapToFilterOptions } from '#pie/utils/mapToFilterOptions';

export const ContactGroupDetail = () => {
  const { t } = useTranslation();
  const contactGroupId = useParams()['contactGroupId']!;
  const marketRoles = useMarketRoleOptions();
  const { data } = useGetCprContactFilters();
  const contactGroupTitle = data?.contactGroups.find(
    (n) => n.id === contactGroupId,
  )?.label;

  const { tableProps, filterProps, toolbarProps } = useDataView({
    columns: (helper) => [
      helper.accessor('marketParty.name', {
        header: t('common.marketparty'),
        meta: { columnWidth: 250 },
      }),
      helper.accessor('organisation.name', {
        header: t('common.subunit'),
      }),
      helper.accessor('marketParty.ean13', {
        header: t('common.ean13'),
      }),
      helper.accessor('marketParty.marketRole', {
        cell: (info) =>
          t(`common.market_role.abbreviation.${info.getValue() as MarketRole}`),
        header: t('common.market_role'),
      }),
      helper.accessor('name', {
        header: t('common.name'),
      }),
      helper.accessor('email', {
        cell: (info) => (
          <TextLink href={`mailto:${info.getValue()}`}>
            {info.getValue()}
          </TextLink>
        ),
        header: t('common.email'),
      }),
      helper.accessor('phone', {
        cell: (info) => (
          <TextLink href={`tel:${info.getValue()}`}>{info.getValue()}</TextLink>
        ),
        header: t('common.phone'),
      }),
    ],
    dateKeys: [],
    filters: [
      {
        id: 'marketPartyIds',
        label: t('common.marketparty'),
        options: (data) => mapToFilterOptions(data?.marketParties),
      },
      {
        id: 'organisationIds',
        label: t('common.subunit'),
        options: (data) => mapToFilterOptions(data?.organisations),
      },
      {
        id: 'marketRoles',
        label: t('common.market_role'),
        options: marketRoles,
      },
    ],
    filtersAtom: cprContactGroupDetailFiltersAtom,
    filtersQuery: getCprContactFilters,
    hardFilters: { contactGroupIds: [contactGroupId] },
    query: getCprContacts,
    sortingAtom: cprContactGroupDetailSortingAtom,
  });

  return (
    <>
      <PageHead
        title={t('cpr_contact_group_detail.header.title', {
          contactGroup: contactGroupTitle,
        })}
      />
      <Page>
        <PageHeader
          title={t('cpr_contact_group_detail.header.title', {
            contactGroup: contactGroupTitle,
          })}
          backHref={'../contactgroep'}
          previousText={t('common.button.back')}
        >
          <CprContactGroupDetailExport />
        </PageHeader>
        <div className="flex items-start gap-6">
          <Card className="flex-1 overflow-x-hidden p-4">
            <Stack gap="md">
              <DataToolbar {...toolbarProps} />
              <DataTable {...tableProps} getRowId={(row) => row.id} />
            </Stack>
          </Card>
          <DataFilters {...filterProps} />
        </div>
      </Page>
    </>
  );
};
